import React, { useEffect, useState, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { authAPI, aboutAPI } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { scrollToForm } from '../utils/adminFormUtils';
import '../styles/adminCommonStyles.css';

const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';

// Columns that contain image data
const IMAGE_COLUMNS = ['Our Approach', 'What We Offer'];

export default function AdminAbout() {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const refetch = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await aboutAPI.getAbout();
      setRawResponse(res);
      console.debug('AdminAbout: raw response:', res);
      const { normalizeItem } = await import('../utils/responseHelpers');
      // normalizeItem handles both axios responses and raw payloads; fall back to res.data if needed
      const normalized = normalizeItem(res) || (res?.data ?? null);
      console.debug('AdminAbout: normalized:', normalized);
      setAboutData(normalized && typeof normalized === 'object' ? normalized : null);
    } catch (err) {
      console.error('AdminAbout fetch error:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to fetch about data';
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, []);
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', columnName: '', value: '' });
  const [editImage, setEditImage] = useState(null);
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // =======================
  // Helper Functions
  // =======================
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const checkUserRole = () => {
    try {
      const hasSuperAdminRole = authAPI.hasRole('superadmin');
      const hasAdminRole = authAPI.hasRole('admin');
      setIsSuperAdmin(hasSuperAdminRole);
      setIsAdmin(hasAdminRole || hasSuperAdminRole);
    } catch (err) {
      setIsSuperAdmin(false);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    checkUserRole();
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // =======================
  // Form Handlers
  // =======================
  // Enhanced image column detection - checks if value contains image path patterns
  const isImageColumn = (columnName, value) => {
    // Check column name contains image keywords
    const columnLower = columnName.toLowerCase();
    if (columnLower.includes('image') || columnLower.includes('photo') || columnLower.includes('approach') || columnLower.includes('offer')) {
      return true;
    }
    
    // Also check if the value looks like an image path
    if (value && typeof value === 'string') {
      return value.includes('/images/') || value.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    }
    
    return false;
  };

  const handleEditClick = (columnName, value) => {
    const resolved = (aboutData && (aboutData[`${columnName}_url`] || aboutData[columnName])) || value || '';
    setEditForm({
      id: aboutData.id || 1,
      columnName: columnName,
      value: resolved || ''
    });
    setEditImage(null);
    setShowEditModal(true);
    setEditError(null);
    scrollToForm();
  };

  const handleEditInput = (e) => {
    setEditForm({ ...editForm, value: e.target.value });
  };

  const handleEditImage = (e) => {
    setEditImage(e.target.files[0]);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);
    try {
      const updateData = {};
      
      if (isImageColumn(editForm.columnName, editForm.value)) {
        if (editImage) {
          // For image columns, send image as FormData
          const formData = new FormData();
          formData.append(editForm.columnName, editImage);
          await aboutAPI.updateAbout(formData, true);
        } else {
          setEditError('Please select an image to upload');
          setEditLoading(false);
          return;
        }
      } else {
        // For text columns, send regular data
        updateData[editForm.columnName] = editForm.value;
        await aboutAPI.updateAbout(updateData);
      }
      
      setShowEditModal(false);
      showSuccess('About data updated successfully!');
      await refetch();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update about data';
      setEditError(typeof errorMsg === 'string' ? errorMsg : 'Failed to update about data');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteAbout = async (aboutId) => {
    if (!isSuperAdmin) {
      alert('Access denied. Only superadmin can delete about content.');
      return;
    }
    const confirmDelete = window.confirm('Are you sure you want to delete this about content? This action cannot be undone.');
    if (!confirmDelete) return;
    setError(null);
    try {
      showSuccess('About content cleared successfully!');
      await refetch();
    } catch (error) {
      setError(error.message || 'Failed to delete about content');
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  // Get all column names (excluding id)
  const getColumnNames = () => {
    if (!aboutData || typeof aboutData !== 'object') return [];
    return Object.keys(aboutData).filter(key => key !== 'id');
  };

  // Get image URL - robust against non-string values
  const getImageUrl = (value) => {
    if (value == null) return null;

    // If value is an object (EditorJS/Sanity block or image object), try to extract a URL
    if (typeof value === 'object') {
      // Handle Buffer-like objects serialized from Node: { type: 'Buffer', data: [47,105,...] }
      if (value && value.type === 'Buffer' && Array.isArray(value.data)) {
        try {
          value = value.data.map((c) => String.fromCharCode(c)).join('');
        } catch (e) {
          value = String(value.data || '');
        }
      }

      // If array, try first item
      if (Array.isArray(value) && value.length > 0) value = value[0];

      // Common shapes: { url }, { src }, { data: { url } }, { file: { url } }
      if (value && typeof value === 'object') {
        if (value.url && typeof value.url === 'string') return value.url;
        if (value.src && typeof value.src === 'string') return value.src;
        if (value.data && value.data.url && typeof value.data.url === 'string') return value.data.url;
        if (value.data && value.data.file && value.data.file.url && typeof value.data.file.url === 'string') return value.data.file.url;
        if (value.file && value.file.url && typeof value.file.url === 'string') return value.file.url;
        // Sanity image object: { asset: { _ref or url } }
        if (value.asset && value.asset.url && typeof value.asset.url === 'string') return value.asset.url;
        // If it's an EditorJS image block: { type: 'image', data: { file: { url } } }
        if (value.type === 'image' && value.data) {
          const d = value.data;
          if (d.url && typeof d.url === 'string') return d.url;
          if (d.file && d.file.url && typeof d.file.url === 'string') return d.file.url;
          if (d.src && typeof d.src === 'string') return d.src;
        }
      }

      // Fallback: stringify
      value = String(value);
    }

    // Ensure we have a string now
    const s = String(value || '');
    if (!s) return null;

    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    if (s.startsWith('/images/')) return `${API_BASE}${s}`;
    if (s.startsWith('/')) return `${API_BASE}${s}`;

    // If it's numeric id, map to /image/{id}
    if (/^\d+$/.test(s)) return `${API_BASE}/image/${s}`;

    // Default: assume relative path
    return `${API_BASE}/${s}`;
  };

  // =======================
  // Render
  // =======================
  return (
    <div className="p-2 sm:p-4 md:p-8 w-full max-w-screen-2xl mx-auto flex flex-col items-center justify-center">
      <div className="w-full flex flex-col items-center sm:items-start gap-4 sm:flex-row sm:justify-between mb-6">
        <h2 className="text-3xl md:text-4xl font-bold text-black mb-2 text-center sm:text-left">Our Story Management</h2>
        <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>
            {isSuperAdmin ? 'Super Admin' : isAdmin ? 'Admin' : 'User'}
          </span>
          <button
            onClick={handleLogout}
            className="admin-btn-danger"
          >
            Logout
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="admin-alert-success">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="admin-alert-error">
          {error}
        </div>
      )}

      {fetchError && (
        <div className="admin-alert-error">
          {fetchError}
        </div>
      )}

      {/* Debug panel for raw response when no about data is available */}
      {!aboutData && !loading && rawResponse && (
        <div className="admin-debug-panel" style={{ marginTop: 12 }}>
          <h4 style={{ marginBottom: 6, fontSize: 14 }}>Debug: raw response (sanitized)</h4>
          <pre style={{ maxHeight: 240, overflow: 'auto', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
            {JSON.stringify(rawResponse.data ?? rawResponse, null, 2)}
          </pre>
        </div>
      )}

      {showEditModal && (
        <div className="admin-modal-overlay-scroll">
          <div className="admin-modal-content admin-modal-form" id="edit-about-form">
            <h3 className="text-xl font-bold mb-4">Edit {editForm.columnName && editForm.columnName.replace(/_/g, ' ')}</h3>
            <form onSubmit={handleEditSubmit}>
              {isImageColumn(editForm.columnName, editForm.value) ? (
                <div className="mb-4">
                  <label className="admin-label">Image</label>
                  {editForm.value && !editImage && (
                    <div className="mb-3 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-2">Current Image:</p>
                      <img
                        src={getImageUrl(editForm.value)}
                        alt="Current"
                        className="h-40 w-auto object-contain rounded-lg border border-gray-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleEditImage}
                    className="admin-input"
                  />
                  {editImage && (
                    <div className="mt-3 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                      <p className="text-sm font-medium text-[#3a8b9e] mb-2">New Image Preview:</p>
                      <img
                        src={URL.createObjectURL(editImage)}
                        alt="New"
                        className="h-40 w-auto object-contain rounded-lg border border-blue-300"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-4">
                  <label className="admin-label">
                    {editForm.columnName && editForm.columnName.replace(/_/g, ' ')}
                  </label>
                  <Editor
                    value={editForm.value}
                    onEditorChange={val => setEditForm(f => ({ ...f, value: val }))}
                    init={{
                      apiKey: TINYMCE_API_KEY,
                      height: 350,
                      menubar: false,
                      branding: false,
                      analytics: false,
                      external_plugins: {},
                      plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                      toolbar:
                        'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                    }}
                  />
                </div>
              )}
              
              {editError && (
                <div className="admin-alert-error-inline">
                  {editError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditError(null);
                  }}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-success"
                  disabled={editLoading}
                >
                  {editLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && (
        <LoadingSpinner message="Loading about content..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
      )}

      {!loading && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead className="admin-table-head">
              <tr>
                <th className="admin-table-header-cell">Column Name</th>
                <th className="admin-table-header-cell">Value</th>
                <th className="admin-table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="admin-table-body-row">
              {!aboutData ? (
                <tr>
                  <td colSpan="3" className="py-8 px-4 text-center text-gray-500">
                    <div className="text-lg">No about content found</div>
                    <div className="text-sm mt-1">Please add about content to get started!</div>
                  </td>
                </tr>
              ) : (
                getColumnNames().map((columnName) => {
                  const displayVal = (aboutData && (aboutData[`${columnName}_url`] || aboutData[columnName])) || '';
                  return (
                  <tr key={columnName} className="hover:bg-blue-50 transition-colors duration-200">
                    <td className="py-4 px-4 font-medium text-gray-900 capitalize">
                      {columnName.replace(/_/g, ' ')}
                    </td>
                    <td className="py-4 px-4">
                      {isImageColumn(columnName, displayVal) ? (
                        displayVal ? (
                          <img
                            src={getImageUrl(displayVal)}
                            alt={columnName}
                            className="h-16 w-16 object-contain rounded border border-gray-300"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className="text-gray-500">No image</span>
                        )
                      ) : (
                        <div className="max-w-xs truncate whitespace-pre-line break-words" title={String(aboutData[columnName])}>
                          <SafeRichText content={String(aboutData[columnName]).length > 50 ? String(aboutData[columnName]).slice(0, 50) + '…' : String(aboutData[columnName])} />
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col gap-2 items-stretch justify-center">
                        <button
                          onClick={() => handleEditClick(columnName, aboutData[columnName])}
                          className="admin-btn-table-edit">
                          Edit
                        </button>
                        <div style={{ display: 'none' }}>
                          {isSuperAdmin ? (
                            <button
                              onClick={() => handleDeleteAbout(aboutData.id || 1)}
                              className="admin-btn-table-danger">
                              Delete
                            </button>
                          ) : (
                            <button
                              disabled
                              className="admin-btn-table-disabled"
                              title="Superadmin role required to delete">
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
